package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.HarvestApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.data.mapper.toDto
import fr.frux.shared.di.IoDispatcher
import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestStatus
import fr.frux.shared.domain.model.HarvestTarget
import fr.frux.shared.domain.model.NearbyHarvests
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.dto.CreateHarvestRequest
import fr.frux.shared.dto.UpdateHarvestStatusRequest
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlin.time.Instant

/**
 * Implementation of HarvestRepository.
 * Handles API calls and error handling, à l'identique de TreeRepositoryImpl.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class HarvestRepositoryImpl(
    private val harvestApi: HarvestApi,
    @IoDispatcher private val dispatcher: CoroutineDispatcher,
) : HarvestRepository {
    companion object {
        private const val TAG = "HarvestRepository"
    }

    override fun getHarvestById(id: Long): Flow<Result<Harvest>> =
        flow {
            emit(
                runCatchingCancellable { harvestApi.getHarvestById(id).toDomain() }
                    .onFailure { Logger.e(TAG, "❌ Chargement de la récolte $id échoué", it) }
                    .mapFailureToAppError(),
            )
        }.flowOn(dispatcher)

    override fun getNearbyHarvests(
        lat: Double,
        lon: Double,
        radius: Double,
        limit: Int,
    ): Flow<Result<NearbyHarvests>> =
        flow {
            val result =
                runCatchingCancellable {
                    val response = harvestApi.getNearbyHarvests(lat = lat, lon = lon, radius = radius, limit = limit)
                    NearbyHarvests(
                        harvests = response.harvests.map { it.toDomain() },
                        totalAvailable = response.total,
                    )
                }.onFailure { Logger.e(TAG, "❌ Récoltes proches indisponibles", it) }
                    .mapFailureToAppError()
            emit(result)
        }.flowOn(dispatcher)

    override suspend fun joinHarvest(id: Long): Result<Harvest> =
        runCatchingCancellable { harvestApi.joinHarvest(id).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Rejoindre la récolte $id échoué", it) }
            .mapFailureToAppError()

    override suspend fun leaveHarvest(id: Long): Result<Harvest> =
        runCatchingCancellable { harvestApi.leaveHarvest(id).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Quitter la récolte $id échoué", it) }
            .mapFailureToAppError()

    override suspend fun createHarvest(
        target: HarvestTarget,
        scheduledDate: Long,
        description: String?,
        maxParticipants: Int?,
        hostedByOwner: Boolean,
    ): Result<Harvest> =
        runCatchingCancellable {
            harvestApi
                .createHarvest(
                    CreateHarvestRequest(
                        treeId = (target as? HarvestTarget.Tree)?.id,
                        siteId = (target as? HarvestTarget.Site)?.id,
                        scheduledDate = Instant.fromEpochMilliseconds(scheduledDate),
                        description = description,
                        maxParticipants = maxParticipants,
                        hostedByOwner = hostedByOwner,
                    ),
                ).toDomain()
        }.onFailure { Logger.e(TAG, "❌ Création de récolte sur $target échouée", it) }
            .mapFailureToAppError()

    override suspend fun updateHarvestStatus(
        id: Long,
        status: HarvestStatus,
    ): Result<Harvest> =
        runCatchingCancellable {
            harvestApi.updateHarvestStatus(id, UpdateHarvestStatusRequest(status.toDto())).toDomain()
        }.onFailure { Logger.e(TAG, "❌ Changement de statut de la récolte $id vers $status échoué", it) }
            .mapFailureToAppError()

    override suspend fun getHarvestsForTree(treeId: Long): Result<List<Harvest>> =
        runCatchingCancellable {
            harvestApi.getHarvestsForTree(treeId).map { it.toDomain() }
        }.onFailure { Logger.e(TAG, "❌ Récoltes de l'arbre $treeId indisponibles", it) }
            .mapFailureToAppError()

    override suspend fun getHarvestsForSite(siteId: Long): Result<List<Harvest>> =
        runCatchingCancellable {
            harvestApi.getHarvestsForSite(siteId).map { it.toDomain() }
        }.onFailure { Logger.e(TAG, "❌ Récoltes du lieu $siteId indisponibles", it) }
            .mapFailureToAppError()

    override suspend fun reportHarvest(id: Long): Result<Unit> =
        runCatchingCancellable { harvestApi.reportHarvest(id) }
            .onFailure { Logger.e(TAG, "❌ Signalement de la récolte $id échoué", it) }
            .mapFailureToAppError()
}
